/**
 * Provider for the session-scoped Update Feed bucket (SE-4861, SE-4865)
 * AND the active category filter selection (SE-4910).
 *
 * Owns a single `useSessionLastFeedVisit` call so all consumers of the
 * captured `sessionLastFeedVisitAt` snapshot share the same value across
 * in-session navigation. This is the contract that backs SE-4861 AC C3
 * ("Count refresh is gated by session boundary, not navigation"): each
 * downstream component (header, divider, list) reads the same captured
 * bucket and so derives the same new-vs-previous partition.
 *
 * Also owns the dropdown's `activeCategoryFilter` state (SE-4910). The
 * dropdown sits inside `UpdateFeed` (the feed shell) but the archive
 * button + mutation live in `UpdateFeedHeader` (a sibling under the
 * home screen). Without a shared parent, the header has no way to read
 * the active filter — so clicking Archive while filtered to a single
 * category was sending `categoryFilter: null` to the BE and archiving
 * EVERY visible update. Lifting filter state here lets:
 *   - the dropdown call `setActiveCategoryFilter` to write
 *   - the feed list read it (via the derived `categories` GraphQL var)
 *   - the header read it for both the archive payload AND the
 *     filter-aware count query
 * without prop-drilling through screen-level layouts.
 *
 * Consumers that mount outside the provider receive a defensive default
 * (`{ sessionLastFeedVisitAt: null, isReady: false, activeCategoryFilter:
 * "ALL", setActiveCategoryFilter: noop }`) — the legacy `/` shell never
 * wraps children with this provider, so the Update Feed primitives must
 * degrade gracefully there even though they are not expected to render
 * in that surface.
 * @module features/update-feed/providers/UpdateFeedSessionProvider
 */
import React, { createContext, useContext, useMemo, useState } from "react";

import { useSessionLastFeedVisit } from "../hooks/useSessionLastFeedVisit";
import {
  ALL_CATEGORIES_VALUE,
  type CategoryFilterValue,
} from "../utils/categoryFilterOptions";

/** Shape of the session context exposed to descendants. */
export interface UpdateFeedSessionContextValue {
  /**
   * The session-stable `lastFeedVisitAt` snapshot. `null` until the
   * underlying query resolves; `null` thereafter for first-ever-session
   * users (BE returns null).
   */
  readonly sessionLastFeedVisitAt: string | null;
  /**
   * `true` once the initial bucket-capture query has settled. Until
   * `true`, count-dependent UI should render the placeholder (e.g. the
   * header reads "Updates" rather than flashing "0 new updates").
   */
  readonly isReady: boolean;
  /**
   * Currently selected dropdown filter value (SE-4910). Defaults to the
   * `ALL` sentinel and resets to ALL on every fresh provider mount per
   * the SE-4863 AC ("Filter does not persist across sessions"). Read by
   * the feed list (drives `categories` GraphQL variable), the header
   * (drives archive payload + count query), and the dropdown (controlled
   * value).
   */
  readonly activeCategoryFilter: CategoryFilterValue;
  /**
   * Setter for the active filter. The dropdown writes through this when
   * the user picks a different option; provider re-renders propagate the
   * change to the header and list in the same render pass.
   * @param next - The new dropdown selection (sentinel or category enum).
   */
  readonly setActiveCategoryFilter: (next: CategoryFilterValue) => void;
}

/** Default value for consumers mounted outside the provider. */
const DEFAULT_VALUE: UpdateFeedSessionContextValue = {
  sessionLastFeedVisitAt: null,
  isReady: false,
  activeCategoryFilter: ALL_CATEGORIES_VALUE,
  setActiveCategoryFilter: () => {
    // Defensive no-op for consumers outside any provider — see the
    // module preamble for why this branch exists.
  },
};

const UpdateFeedSessionContext =
  createContext<UpdateFeedSessionContextValue>(DEFAULT_VALUE);

/** Props for `UpdateFeedSessionProvider`. */
export interface UpdateFeedSessionProviderProps {
  /**
   * Gates the underlying `useSessionLastFeedVisit` effects. Pass `true`
   * once sign-in has settled so the session-capture query does not fire
   * before auth resolves; pass `false` to keep the effects dormant.
   * Mirrors the `enabled` prop on `useSessionLastFeedVisit`.
   */
  readonly enabled: boolean;
  /** Subtree that should observe the captured session bucket. */
  readonly children: React.ReactNode;
}

/**
 * Wrap the authenticated app subtree so siblings share one captured bucket.
 * @param props - Provider props
 * @param props.enabled - Gates the underlying session-capture effects
 * @param props.children - Subtree that observes the captured bucket
 * @returns The provider element wrapping `children`
 */
export function UpdateFeedSessionProvider({
  enabled,
  children,
}: UpdateFeedSessionProviderProps) {
  const { sessionLastFeedVisitAt, isReady } = useSessionLastFeedVisit({
    enabled,
  });

  // SE-4910: filter state lifted from `UpdateFeedContainer`. Initial
  // value is the ALL sentinel and the state lives inside the provider —
  // unmounting the provider subtree (e.g. tab change to /scouting and
  // back) resets the filter, which honors the SE-4863 AC "Filter does
  // not persist across sessions".
  const [activeCategoryFilter, setActiveCategoryFilter] =
    useState<CategoryFilterValue>(ALL_CATEGORIES_VALUE);

  // `setActiveCategoryFilter` is the React-stable setter from `useState` —
  // it never changes identity, so we forward it directly through context
  // without an extra `useCallback` wrapper. Consumers that subscribe to
  // setter identity (e.g. `useEffect` dep arrays) get a stable reference
  // for the lifetime of the provider.
  const value = useMemo<UpdateFeedSessionContextValue>(
    () => ({
      sessionLastFeedVisitAt,
      isReady,
      activeCategoryFilter,
      setActiveCategoryFilter,
    }),
    [sessionLastFeedVisitAt, isReady, activeCategoryFilter]
  );

  return (
    <UpdateFeedSessionContext.Provider value={value}>
      {children}
    </UpdateFeedSessionContext.Provider>
  );
}

UpdateFeedSessionProvider.displayName = "UpdateFeedSessionProvider";

/**
 * Read the session-captured Update Feed bucket from context.
 *
 * Returns the defensive default outside a provider — the legacy `/`
 * shell does not render Update Feed primitives, but tests and the
 * bare-route fallback must not crash on missing context.
 * @returns The current session-context value
 */
export function useUpdateFeedSession(): UpdateFeedSessionContextValue {
  return useContext(UpdateFeedSessionContext);
}
